
import * as AppCommands from "@/assets/scripts/Application/Commands";
import * as PageCommands from "@/assets/scripts/PageEditor/Commands";
// Dependencies
import { Alignment, EnumProperty } from "@/assets/scripts/Page";
import { defineComponent, PropType, ref } from "vue";
// Components
import FocusBox from "@/components/Containers/FocusBox.vue";
import OptionsList from "./OptionsList.vue";

export default defineComponent({
  name: "EnumField",
  setup() {
    return { search: ref<HTMLElement | null>(null) };
  },
  props: {
    property: {
      type: Object as PropType<EnumProperty>,
      required: true
    },
    maxHeight: {
      type: Number,
      default: 200
    }
  },
  data() {
    return {
      select: this.property.value,
      showMenu: false,
      showSearch: false,
      searchTerm: ""
    }
  },
  computed: {

    /**
     * Returns the field's alignment classes.
     * @returns
     *  The field's alignment classes.
     */
    alignment(): string {
      switch(this.property.alignment) {
        case Alignment.TopLeft:
        case Alignment.MiddleLeft:
        case Alignment.BottomLeft:
          return "align-left";
        case Alignment.TopCenter:
        case Alignment.MiddleCenter:
        case Alignment.BottomCenter:
          return "align-center";
        case Alignment.TopRight:
        case Alignment.MiddleRight:
        case Alignment.BottomRight:
          return "align-right";
      }
    },

    /**
     * Tests if the null option is selected.
     * @returns
     *  True if the null option is selected, false otherwise.
     */
    isNull(): boolean {
      return this.property.value === null;
    },
    
    /**
     * Returns the enum's options.
     * @returns
     *  The enum's options.
     */
    options(): { value: string | null, text: string }[] {
      let options: { value: string | null, text: string }[] = [];
      if(this.searchTerm === "") {
        options.push({ value: null, text: "None" });
      }
      let st = this.searchTerm.toLocaleLowerCase();
      for(let [value, text] of this.property.validOptions) {
        if(st === "" || text.toLocaleLowerCase().includes(st)) {
          options.push({ value, text });
        }
      }
      return options;
    },

    /**
     * Returns the enum's current selection text.
     * @returns
     *  The enum's current selection text.
     */
    selectText(): string {
      if(this.select !== null) {
        return this.property.options.get(this.select)!
      } else {
        return "None";
      }
    },

    /**
     * Returns the scrollbox's style.
     * @returns
     *  The scrollbox's style.
     */
    style(): { maxHeight: string } {
      return { maxHeight: `${ this.maxHeight }px` };
    }

  },
  emits: ["execute"],
  methods: {

    /**
     * Field focus in behavior.
     */
    onFocusIn() {
      // Open menu
      this.showMenu = true;
      // Show search
      this.showSearch = true;
      // Focus search
      setTimeout(() => {
        this.search?.focus();
      }, 0);
      // Execute select command
      let cmd = PageCommands.selectAtomicProperty(this.property);
      this.$emit("execute", cmd);
    },

    /**
     * Field focus out behavior.
     */
    onFocusOut() {
      // Close menu
      this.showMenu = false;
      // Hide search
      this.showSearch = false;
      this.searchTerm = "";
      // Refresh value
      this.refreshValue();
      // Execute deselect command
      let cmd = PageCommands.deselectAtomicProperty(this.property);
      this.$emit("execute", cmd);
    },

    /**
     * Search field input behavior.
     */
    onSearchInput() {
      this.select = null;
      if(this.searchTerm === "") {
        this.select = this.property.value;
        return;
      }
      let st = this.searchTerm.toLocaleLowerCase();
      for(let [value, text] of this.property.validOptions) {
        if(text.toLocaleLowerCase().includes(st)) {
          this.select = value;
          return;
        }
      }
    },

    /**
     * Search field keydown behavior.
     * @param event
     *  The keydown event.
     */
    onSearchKeyDown(event: KeyboardEvent) {
      let field = event.target as HTMLInputElement;
      if(field.selectionStart !== field.selectionEnd) {
        return;
      }
      let idx;
      let options = this.options;
      switch(event.key) {
        case "ArrowUp":
          idx = options.findIndex(o => o.value === this.select);
          if(0 < idx) {
            this.select = options[idx - 1].value;
          }
          break;
        case "ArrowDown":
          idx = options.findIndex(o => o.value === this.select);
          if(idx < options.length - 1) {
            this.select = options[idx + 1].value;
          }
          break;
        case "Tab":
        case "Enter":
          this.updateProperty(this.select);
          // Force search field out of focus
          this.search!.blur();
          break;
      }
    },

    /**
     * Updates the field's property value.
     * @param value
     *  The property's new value.
     */
    updateProperty(value: string | null) {
      if(this.property.value !== value) {
        // Execute update command
        let cmd = PageCommands.setEnumProperty(this.property, value);
        this.$emit("execute", cmd);
      }
    },

    /**
     * Updates the field's text value.
     */
    refreshValue() {
      this.select = this.property.value
    }
    
  },
  watch: {
    "property"() {
      // Refresh value
      this.refreshValue();
    },
    "property.value"() {
      // Refresh value
      this.refreshValue();
    }
  },
  mounted() {
    // Update field property value
    this.refreshValue();
    // Execute mount command
    let cmd = AppCommands.mountProperty(this.property, this.$el);
    this.$emit("execute", cmd);
  },
  unmounted() {
    // Execute destroy command
    let cmd = AppCommands.destroyProperty(this.property);
    this.$emit("execute", cmd);
  },
  components: { FocusBox, OptionsList }
});
